import { useCallback, useEffect, useRef, useState } from 'react';

// material-ui
import { Box, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// assets
import { IconChevronLeft, IconChevronRight } from '@tabler/icons';

/*
 * The design's page header and its pill rail, and the left/right arrows that
 * scroll it.
 *
 * This is the one module that decides what a tab strip looks like. Two surfaces
 * render it — the section strip above every section page, and the admin strip —
 * and they must not merely look alike, they must BE alike. A private copy of the
 * pill in each of them would drift the first time one was nudged, which is the
 * same failure this app has already had twice (the Learnings section ended up
 * with two different tab lists; the sidebar and the strip ended up disagreeing
 * about which QIC year is live).
 *
 * That failure is not hypothetical here: it is exactly what happened once
 * already. SectionTabs grew its own `pillStyles` copy during a restyle, and the
 * two strips silently became two designs again. Hence the shape below — the pill
 * is `pillSx`, the card is `cardSx`, the ornament is `CornerDecor`, and the whole
 * header is `PageHeader`, so a caller states its contents and nothing else.
 *
 * The rail scrolls rather than wrapping, and it never shows a scrollbar: the
 * arrows are the affordance. Everything that could introduce a literal colour
 * lives in this file, so "what colours does the strip use" has one answer.
 */

/*
 * The three colours the design's header needs that the palette does not carry.
 *
 * They arrived with the mock, which specifies them directly, and there is no
 * token equal to any of them. They are collected here rather than scattered
 * through the callers so the exception is one paragraph instead of a hunt — and
 * so that if they are ever mapped onto tokens, it is one edit.
 *
 * `NAVY` and `MUTED` are light-mode only: dark mode falls back to `text.primary`
 * and `text.secondary`, which is what the header used before the mock. The
 * ornament keeps `ORNAMENT_BLUE` in both modes, because it is the same mark.
 */
const NAVY = '#0B2A6F';
const MUTED = '#5B6B99';
const ORNAMENT_BLUE = '#4DB5FF';

/*
 * The space between two pills, and the divider that sits in the middle of it.
 * One number, because the divider is positioned from it — two numbers would be
 * two numbers that have to agree by hand, and would stop agreeing.
 */
const PILL_GAP = 16;

/**
 * The design's card: the rounded white panel the heading and the rail sit in.
 *
 * A function of the theme because the shadow is blue-tinted and mode-aware — the
 * design's soft glow, which a flat elevation string cannot express.
 *
 * `overflow: 'hidden'` is what clips the corner ornament to the card's radius; it
 * is safe beside the admin strip's dropdown because MUI's <Menu> renders into a
 * portal, outside this box.
 */
export const cardSx = (theme) => {
    const blue = theme.palette.secondary.main;
    const dark = theme.palette.mode === 'dark';

    return {
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRadius: '28px',
        mt: '18px',
        mb: '1rem',
        p: { xs: '16px', sm: '24px' },
        boxShadow: `0 12px 40px ${alpha(blue, dark ? 0.18 : 0.1)}`
    };
};

/**
 * The capsule holding the pills.
 *
 * `overflowY: 'hidden'` is deliberate rather than incidental. Only `overflowX`
 * was ever set, and CSS promotes a lone `visible` to `auto` when the other axis
 * is not visible — so the vertical axis was already a scrollport by accident.
 * Pinning it says out loud what the active pill's underline depends on: the bar
 * is drawn inside this box's bottom padding (`p` below), so it stays inside the
 * padding box and the rail's own overflow cannot clip it.
 *
 * `flex`/`minWidth` because this is a flex child of ScrollRail's outer row: it
 * takes the leftover width, and `minWidth: 0` is what lets it shrink below its
 * content instead of pushing the arrows out of the card.
 *
 * No margin. The gap above the rail belongs to PageHeader, which is what puts the
 * rail below the heading on both headers — a caller should not be adding it.
 */
export const railSx = (theme) => ({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: `${PILL_GAP}px`,
    p: { xs: '5px 5px 11px', sm: '6px 6px 12px' },
    borderRadius: '999px',
    bgcolor: alpha(theme.palette.secondary.main, 0.08),
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' }
});

/**
 * One pill.
 *
 * `active` fills it in the app's blue with white text, lifts it on a blue-tinted
 * shadow, and drops the divider — a rule between two inert neighbours reads as a
 * separator, but one between a filled pill and its neighbour reads as an mistake.
 *
 * `divider` is passed in rather than derived, because only the caller knows where
 * the current pill is: the rule is skipped for the first pill and for the one
 * immediately after the active pill, and that is a property of the row, not of
 * any single pill in it.
 *
 * There is deliberately no underline bar under the active pill. One was drawn
 * here once and then commented out during the restyle; the design keeps the solid
 * fill alone. Its removal is pinned by a test, so it cannot come back by accident.
 *
 * Callers mark the active pill with `data-rail-active="true"` so the rail can
 * find it to scroll into view — see ScrollRail.
 */
export const pillSx = (theme, active, divider) => {
    const blue = theme.palette.secondary.main;
    const dark = theme.palette.mode === 'dark';

    return {
        position: 'relative',
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        height: 42,
        mt:'0.3rem',
        px: { xs: '12px', sm: '16px' },
        borderRadius: '999px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontSize: { xs: '14px', sm: '15px' },
        fontWeight: 700,
        letterSpacing: '-0.01em',
        color: active ? theme.palette.common.white : dark ? theme.palette.text.primary : NAVY,
        backgroundColor: active ? blue : 'transparent',
        boxShadow: active ? `0 8px 20px ${alpha(blue, 0.35)}` : 'none',
        transition: 'background-color .2s, color .2s, box-shadow .2s',
        '&:hover': active ? undefined : { backgroundColor: alpha(blue, 0.08) },
        '&:focus-visible': { outline: `2px solid ${blue}`, outlineOffset: 2 },
        // Straddles the middle of the gap to the previous pill, so the rule is
        // centred in the space rather than crowding the pill it follows. The
        // offset is half the rail's gap — the same one number, not a second.
        '&::before': divider
            ? {
                  content: '""',
                  position: 'absolute',
                  left: `-${PILL_GAP / 2}px`,
                  top: '50%',
                  width: '1px',
                  height: 24,
                  transform: 'translateY(-50%)',
                  backgroundColor: alpha(blue, 0.3)
              }
            : undefined
    };
};

/**
 * Corner ornament from the design: a big blue petal clipped by the card corner, a
 * smaller leaf overlapping it (outlined in the card colour so it reads as a cut),
 * and a 3x3 dot grid beside it. Purely decorative, hidden on phones.
 *
 * `aria-hidden` and `pointerEvents: 'none'` because it is not content: it must
 * carry no meaning to a screen reader and must not eat clicks meant for whatever
 * it overlaps.
 *
 * Exported for the product dialog's header, which wears the same ornament. A
 * second caller copying this SVG is exactly the drift the note above warns about,
 * so it is handed over instead — `blue` is the gradient's start and the leaf's
 * outline is stroked in `cardBg`, the colour of whatever it sits on.
 */
const CornerDecor = ({ blue, cardBg }) => (
    <Box
        aria-hidden="true"
        sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', top: 0, right: 0, pointerEvents: 'none', lineHeight: 0 }}
    >
        {/* dot grid */}
        <svg width="30" height="30" viewBox="0 0 30 30" style={{ position: 'absolute', top: 22, right: 96 }}>
            {[0, 1, 2].map((r) =>
                [0, 1, 2].map((c) => (
                    <circle
                        key={`${r}${c}`}
                        cx={3 + c * 12}
                        cy={3 + r * 12}
                        r="2.2"
                        fill={ORNAMENT_BLUE}
                        opacity={r === 2 && c === 2 ? 0.35 : 0.8}
                    />
                ))
            )}
        </svg>

        {/* petal + leaf */}
        <svg width="96" height="130" viewBox="0 0 130 175" style={{ display: 'block' }}>
            <defs>
                <linearGradient id="st-petal" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor={blue} />
                    <stop offset="1" stopColor={ORNAMENT_BLUE} />
                </linearGradient>
                <linearGradient id="st-leaf" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor={blue} />
                    <stop offset="1" stopColor={ORNAMENT_BLUE} />
                </linearGradient>
            </defs>
            <path d="M35 0 H130 V168 C104 112 74 78 55 58 C40 42 30 20 35 0 Z" fill="url(#st-petal)" />
            <path
                d="M0 80 C30 70 76 82 106 120 C116 134 123 150 128 168 C95 150 46 126 0 80 Z"
                fill="url(#st-leaf)"
                stroke={cardBg}
                strokeWidth="5"
                strokeLinejoin="round"
            />
        </svg>
    </Box>
);

/**
 * The whole page header: the card, the corner ornament, the badge with the
 * title and subtitle, and then the rail.
 *
 * This is the component that makes "the same strip on every page" a fact rather
 * than an intention. Both surfaces render this, so neither can acquire its own
 * card radius, its own heading size, or its own ornament — and when the design
 * changes, it changes for both at once.
 *
 * `children` is the rail. It is wrapped rather than passed through so the spacing
 * below the heading and the stacking above the ornament are decided here; a
 * caller that had to remember `position: 'relative'` on its own rail would be a
 * caller that could forget it.
 */
const PageHeader = ({ icon: PageIcon, title, sub, children }) => {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const blue = theme.palette.secondary.main;

    return (
        <Box sx={cardSx(theme)}>
            <CornerDecor blue={blue} cardBg={theme.palette.background.paper} />

            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: { xs: '14px', sm: '18px' } }}>
                {PageIcon && (
                    <Box
                        sx={{
                            width: { xs: 46, sm: 56 },
                            height: { xs: 46, sm: 56 },
                            borderRadius: '16px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'common.white',
                            background: `linear-gradient(135deg, ${blue} 0%, ${alpha(blue, 0.85)} 100%)`,
                            boxShadow: `0 8px 18px ${alpha(blue, 0.3)}`
                        }}
                    >
                        <PageIcon size={26} strokeWidth={1.9} />
                    </Box>
                )}
                {/* `pr` reserves the ornament's width so a long title wraps before it,
                    rather than running underneath it. Matches the ornament's own
                    96px offset. */}
                <Box sx={{ minWidth: 0, pr: { sm: '96px' } }}>
                    <Typography
                        component="h1"
                        sx={{
                            m: 0,
                            fontSize: { xs: '22px', sm: '28px' },
                            fontWeight: 800,
                            letterSpacing: '-0.025em',
                            lineHeight: 1.25,
                            color: dark ? theme.palette.text.primary : NAVY
                        }}
                    >
                        {title}
                    </Typography>
                    {sub && (
                        <Typography
                            component="p"
                            sx={{
                                mt: '2px',
                                mb: 0,
                                fontSize: { xs: '13px', sm: '15px' },
                                lineHeight: 1.5,
                                color: dark ? theme.palette.text.secondary : MUTED
                            }}
                        >
                            {sub}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* The rail is optional: OmniWealth's card carries no row, and a
                section whose tabs have not arrived draws none either (the
                design hides an empty one). The wrapper takes its own 18px of
                space, so it is skipped rather than rendered empty — otherwise
                the card ends in a gap that reads as a missing row. */}
            {children ? <Box sx={{ position: 'relative', mt: '18px' }}>{children}</Box> : null}
        </Box>
    );
};

export { PageHeader, CornerDecor };

/**
 * A horizontal rail of pills with left/right arrows and no scrollbar.
 *
 * `activeKey` is any value that changes when the current pill changes. It is
 * deliberately not an index: the admin rail resolves its active entry by
 * prefix-matching the path against a flattened tree, and a rail that indexed
 * `children[i]` would couple that to DOM order.
 */
const ScrollRail = ({ children, label, activeKey, sx }) => {
    const theme = useTheme();

    const railRef = useRef(null);
    const firstRun = useRef(true);
    const [can, setCan] = useState({ left: false, right: false });

    /*
     * Whether either arrow has anywhere to go.
     *
     * The 1px slack is not decoration. Layout widths are fractional, so at the
     * true end of the rail `scrollLeft + clientWidth` can land half a pixel
     * short of `scrollWidth` and leave the right arrow lit forever, inviting a
     * click that does nothing.
     *
     * The bail-out below is what makes it safe to call this far more often than
     * it needs to run: returning `prev` unchanged means React skips the
     * re-render, so the measure-every-render effect beneath cannot loop.
     */
    const measure = useCallback(() => {
        const el = railRef.current;
        if (!el) return;

        const { scrollLeft, scrollWidth, clientWidth } = el;
        const next = {
            left: scrollLeft > 1,
            right: scrollLeft + clientWidth < scrollWidth - 1
        };

        setCan((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
    }, []);

    /*
     * No dependency array, on purpose. The things that change this rail's
     * overflow are not nameable: QIC's years arrive from an API and the admin
     * tree follows the stored role, so a pill can appear without any prop here
     * changing. Measuring after every render is the honest way to catch that.
     * MUI's own scrollable <Tabs> — which this replaces — does the same.
     */
    useEffect(() => {
        measure();
    });

    // A resize changes what fits, so it changes the answer.
    useEffect(() => {
        const el = railRef.current;
        if (!el) return undefined;

        // ResizeObserver catches the rail being resized by its own container;
        // the window listener is the fallback for environments without it.
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(measure);
            observer.observe(el);
        }
        window.addEventListener('resize', measure);

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [measure]);

    /*
     * Bring the current pill into view when it changes.
     *
     * Not a nicety: MUI's scrollable <Tabs> did this, so the admin strip already
     * opens with the current entry visible, and dropping it would leave a long
     * rail scrolled to its start with nothing lit.
     *
     * getBoundingClientRect arithmetic rather than offsetLeft — which depends on
     * which ancestor happens to be positioned — and rather than scrollIntoView,
     * which can scroll the whole page on a freshly-loaded route.
     */
    useEffect(() => {
        const el = railRef.current;
        if (!el) return;

        const pill = el.querySelector('[data-rail-active="true"]');
        if (!pill) return;

        const target =
            pill.getBoundingClientRect().left -
            el.getBoundingClientRect().left +
            el.scrollLeft -
            (el.clientWidth - pill.offsetWidth) / 2;

        el.scrollTo({
            left: Math.max(0, Math.min(target, el.scrollWidth - el.clientWidth)),
            // Snap on arrival so a page load does not animate, glide after.
            behavior: firstRun.current ? 'auto' : 'smooth'
        });
        firstRun.current = false;
    }, [activeKey]);

    /*
     * One page per click, with a floor. The 0.8 overlap means a pill at the edge
     * is still partly visible afterwards, so the click never loses your place;
     * the floor keeps it usable in a narrow card. Because `measure` runs on
     * scroll, a smooth scroll updates the arrows live and the right one fades
     * out as the end arrives.
     */
    const scrollByDir = (dir) => {
        const el = railRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 160), behavior: 'smooth' });
    };

    /*
     * The arrow buttons.
     *
     * Both are always rendered and always occupy their 32px; only their
     * appearance toggles. That is load-bearing, not laziness. An arrow that
     * unmounted would narrow the rail, which moves the very thresholds the
     * flags above are read from, which can bring the other arrow back — the two
     * of them oscillating, each appearing because the other vanished. Freezing
     * the geometry is what makes the feedback loop impossible rather than
     * merely unlikely.
     *
     * The cost is ~80px of gutter, which is why they are hidden entirely below
     * `sm`: on a phone the rail goes full width and keeps its touch-swipe.
     */
    const arrow = (dir) => {
        const Icon = dir < 0 ? IconChevronLeft : IconChevronRight;

        return (
            <IconButton
                onClick={() => scrollByDir(dir)}
                disabled={dir < 0 ? !can.left : !can.right}
                aria-label={dir < 0 ? 'Scroll tabs left' : 'Scroll tabs right'}
                size="small"
                sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    color: 'text.secondary',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                    '&.Mui-disabled': { opacity: 0, visibility: 'hidden' }
                }}
            >
                <Icon size={18} stroke={1.8} aria-hidden="true" />
            </IconButton>
        );
    };

    return (
        <Box sx={[{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', minWidth: 0 }, sx]}>
            {arrow(-1)}
            <Box ref={railRef} role="tablist" aria-label={label} onScroll={measure} sx={railSx(theme)}>
                {children}
            </Box>
            {arrow(1)}
        </Box>
    );
};

export default ScrollRail;
